import { getAuthToken, handleTokenExpiration } from '../utils/tokenUtils'
import useMaskStore from '../stores/maskStore'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

/**
 * do not remove, modify as needed
 * 
 * Masks API Service
 * Provides methods for interacting with the masks API endpoints.
 */
const masksApi = {
  /**
   * do not remove, modify as needed
   * 
   * Creates a new mask
   * 
   * Input Parameters:
   * @param {Object} maskData - Mask creation parameters
   *   @param {string} maskData.generationId - ID of the parent generation
   *   @param {string} maskData.versionId - ID of the version to mask
   *   @param {string} maskData.name - Name of the mask
   *   @param {string} maskData.type - Type of mask ('brush' or 'description')
   *   @param {string} [maskData.prompt] - Required for description masks
   *   @param {string} [maskData.negativePrompt] - Optional for description masks
   * 
   * Expected Response:
   * @returns {Promise<Object>} The created mask object
   *   @returns {string} .id - Mask ID
   *   @returns {string} .status - Status of the mask
   *   @returns {string} .createdAt - Creation timestamp
   */
  createMask: async (maskData) => {
    const token = getAuthToken()
    const setLoading = useMaskStore.getState().setLoading
    const addMask = useMaskStore.getState().addMask
    const startPolling = useMaskStore.getState().startPolling
    
    try {
      setLoading(true)
      
      const response = await fetch(`${API_BASE_URL}/masks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(maskData)
      })

      if (response.status === 400) {
        const error = await response.json()
        if (error.message === 'Invalid Token') {
          handleTokenExpiration()
        }
        throw new Error(error.message || 'Failed to create mask')
      }

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to create mask')
      }

      const newMask = await response.json()
      
      // Ensure createdAt is set when adding the mask
      const maskWithTimestamp = {
        ...newMask.mask,
        createdAt: newMask.mask.createdAt || new Date().toISOString()
      }
      addMask(maskWithTimestamp)
      
      // Start polling for status updates if mask is generating
      if (maskWithTimestamp.status === 'generating') {
        startPolling(maskWithTimestamp.id)
      }

      return { ...newMask, mask: maskWithTimestamp }
    } finally {
      setLoading(false)
    }
  },

  /**
   * do not remove, modify as needed
   * 
   * Lists masks for a generation version
   * 
   * Input Parameters:
   * @param {string} generationId - ID of the generation
   * @param {string} versionId - ID of the version
   * 
   * Expected Response:
   * @returns {Promise<Object>} Object containing masks array
   *   @returns {Array<Object>} .masks - Array of mask objects
   */
  listMasks: async (generationId, versionId) => {
    const token = getAuthToken()
    const { setMasks, setLoading, startPolling, setError } = useMaskStore.getState()
    
    if (!generationId || !versionId) {
      setError('Missing required parameters')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(
        `${API_BASE_URL}/masks/generation/${generationId}/version/${versionId}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to list masks')
      }

      const data = await response.json()
      
      // Ensure all masks have createdAt timestamps
      const masksWithTimestamps = data.masks.map(mask => ({
        ...mask,
        createdAt: mask.createdAt || new Date().toISOString()
      }))
      setMasks(masksWithTimestamps)

      // Start polling for any generating masks
      masksWithTimestamps.forEach(mask => {
        if (mask.status === 'generating') {
          startPolling(mask._id)
        }
      })

      return { ...data, masks: masksWithTimestamps }
    } catch (error) {
      console.error('List masks error:', error)
      setError(error.message || 'Failed to list masks')
      throw error
    } finally {
      setLoading(false)
    }
  },

  /**
   * do not remove, modify as needed
   * 
   * Gets a mask's image data
   * 
   * Input Parameters:
   * @param {string} maskId - ID of the mask to retrieve
   * 
   * Expected Response:
   * @returns {Promise<Object>} Object containing mask URL
   *   @returns {string} .url - Object URL for the mask image
   */
  getMask: async (maskId) => {
    const token = getAuthToken()
    
    const response = await fetch(`${API_BASE_URL}/masks/${maskId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get mask')
    }

    const blob = await response.blob()
    return {
      url: URL.createObjectURL(blob)
    }
  },

  /**
   * do not remove, modify as needed
   * 
   * Checks the status of a mask
   * 
   * Input Parameters:
   * @param {string} maskId - ID of the mask to check
   * 
   * Expected Response:
   * @returns {Promise<Object>} Status object
   *   @returns {string} .status - Current status of the mask
   *   @returns {string} [.error] - Error message if status is 'failed'
   *   @returns {number} [.estimatedWait] - Estimated wait time in seconds
   */
  checkStatus: async (maskId) => {
    const token = getAuthToken()
    const { updateMaskStatus, getMaskById } = useMaskStore.getState()
    
    const response = await fetch(`${API_BASE_URL}/masks/${maskId}/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to check mask status')
    }

    const data = await response.json()
    const currentMask = getMaskById(maskId)
    
    // Always update if status changed or if estimatedWait is different
    if (!currentMask || 
        currentMask.status !== data.status || 
        currentMask.estimatedWait !== data.estimatedWait) {
      updateMaskStatus(maskId, {
        status: data.status,
        error: data.error,
        estimatedWait: data.estimatedWait
      })
    }

    return data
  }
}

export default masksApi